import React from 'react';
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog.tsx';
import { SystemLog, SystemLogDetails } from '../types/costwand';
import { useAllLookups } from '../hooks/useAllLookups.ts';
import { Spinner } from './ui/spinner.tsx';

interface SystemLogDetailsDialogProps {
  logEntry: SystemLog | null;
}

// Helper to map foreign key field names to their corresponding lookup table names
const getTableNameForForeignKey = (fieldName: string): string | null => {
  if (fieldName.endsWith('_id')) {
    const baseName = fieldName.replace(/_id$/, '');
    // Direct mappings
    if (baseName === 'vendor') return 'vendors';
    if (baseName === 'family') return 'families';
    if (baseName === 'subfamily') return 'subfamilies';
    if (baseName === 'barcode_1') return 'barcode_1';
    if (baseName === 'barcode_2') return 'barcode_2';
    if (baseName === 'currency') return 'currencies';
    if (baseName === 'unit_of_measure') return 'units_of_measure';
    if (baseName === 'tax_profile') return 'surcharges';
    if (baseName === 'product') return 'products';
    if (baseName === 'process') return 'processes';
    // Special handling for 'component_id' which can refer to products or composites
    if (baseName === 'component') return 'products'; // 'products' lookup contains both products and composites
  }
  return null;
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object' && !Array.isArray(value)) return JSON.stringify(value);
  return String(value);
};

const renderDetails = (
  data: Record<string, any>,
  resolveId: (tableName: string, id: number | string | null | undefined) => string,
  level: number = 0,
  contextTableName: string | null = null // This is the table_name from the SystemLog entry or a parent context
): React.ReactNode => {
  if (!data || typeof data !== 'object') {
    return <span className="text-muted-foreground">{formatValue(data)}</span>;
  }

  const indent = level * 2; // 2 spaces per level
  const items: React.ReactNode[] = [];

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = data[key];
      let tableNameForLookup: string | null = null;

      if (key === 'id') {
        // The primary ID of the current object being rendered
        tableNameForLookup = contextTableName;
      } else if (key.endsWith('_id')) {
        // A foreign key ID
        tableNameForLookup = getTableNameForForeignKey(key);
      }

      // Handle nested objects (e.g., _labels, or nested component/process details)
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        if (key === '_labels') {
          // _labels are already human-readable, just display them
          items.push(
            <div key={key} style={{ marginLeft: `${indent}ch` }}>
              <span className="font-semibold">{key}:</span>
              <ul className="list-disc list-inside ml-4">
                {Object.entries(value).map(([labelKey, labelValue]) => (
                  <li key={labelKey} style={{ marginLeft: `${(level + 1) * 2}ch` }}>
                    <span className="font-semibold">{labelKey}:</span> {formatValue(labelValue)}
                  </li>
                ))}
              </ul>
            </div>
          );
        } else if (key === 'components' && contextTableName === 'composites') {
          // Special handling for components object with numeric keys in composite logs
          // Convert the object to an array of objects for consistent rendering
          const componentsArray = Object.entries(value).map(([componentKey, componentValue]) => ({
            id: componentKey, // Use the numeric key as an ID for display purposes
            ...(componentValue as Record<string, any>),
          }));
          items.push(
            <div key={key} style={{ marginLeft: `${indent}ch` }}>
              <span className="font-semibold">{key}:</span>
              <ul className="list-disc list-inside ml-4">
                {componentsArray.map((item: any, idx: number) => (
                  <li key={`${key}-${idx}`} style={{ marginLeft: `${(level + 1) * 2}ch` }}>
                    {renderDetails(item, resolveId, level + 2, 'products')} {/* component_id refers to products/composites */}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        else {
          // For other nested objects, recurse.
          // Determine the contextTableName for the next level of recursion.
          let nextContextTableName = contextTableName;
          if (key === 'composition') {
            nextContextTableName = 'composites'; // When inside 'composition', the primary context is composites
          }
          // No need to set 'products' or 'processes' here, as they are handled when iterating arrays of components/processes
          items.push(
            <div key={key} style={{ marginLeft: `${indent}ch` }}>
              <span className="font-semibold">{key}:</span>
              <ul className="list-disc list-inside ml-4">
                {renderDetails(value, resolveId, level + 1, nextContextTableName)}
              </ul>
            </div>
          );
        }
      } else if (Array.isArray(value)) {
        // Handle arrays of values (e.g., ["old_value", "new_value"]) or arrays of objects (e.g., processes within components)
        if (value.length === 2 && tableNameForLookup) { // ID change (e.g., currency_id: [1, 2])
          items.push(
            <li key={key} style={{ marginLeft: `${indent}ch` }}>
              <span className="font-semibold">{key}:</span>
              <span className="text-red-500 line-through mr-2">
                {resolveId(tableNameForLookup, value[0])} (ID: {value[0]})
              </span>
              <span className="text-green-500">
                {resolveId(tableNameForLookup, value[1])} (ID: {value[1]})
              </span>
            </li>
          );
        } else if (value.length === 2) { // Simple value change (e.g., description: ["old", "new"])
          items.push(
            <li key={key} style={{ marginLeft: `${indent}ch` }}>
              <span className="font-semibold">{key}:</span>
              <span className="text-red-500 line-through mr-2">{formatValue(value[0])}</span>
              <span className="text-green-500">{formatValue(value[1])}</span>
            </li>
          );
        } else if (value.every(item => typeof item === 'object' && item !== null)) {
          // Array of objects (e.g., components, processes)
          let nextContextTableNameForArrayItems = contextTableName;
          if (key === 'components') {
            nextContextTableNameForArrayItems = 'products'; // component_id refers to products/composites
          } else if (key === 'processes') {
            nextContextTableNameForArrayItems = 'processes'; // process_id refers to processes
          }

          items.push(
            <div key={key} style={{ marginLeft: `${indent}ch` }}>
              <span className="font-semibold">{key}:</span>
              <ul className="list-disc list-inside ml-4">
                {value.map((item: any, idx: number) => (
                  <li key={`${key}-${idx}`} style={{ marginLeft: `${(level + 1) * 2}ch` }}>
                    {renderDetails(item, resolveId, level + 2, nextContextTableNameForArrayItems)}
                  </li>
                ))}
              </ul>
            </div>
          );
        } else { // Generic array of primitives or mixed types
          items.push(
            <li key={key} style={{ marginLeft: `${indent}ch` }}>
              <span className="font-semibold">{key}:</span> {formatValue(value)}
            </li>
          );
        }
      } else if (tableNameForLookup) { // Single ID field
        items.push(
          <li key={key} style={{ marginLeft: `${indent}ch` }}>
            <span className="font-semibold">{key}:</span>{' '}
            {resolveId(tableNameForLookup, value)} (ID: {value})
          </li>
        );
      } else { // Regular field
        items.push(
          <li key={key} style={{ marginLeft: `${indent}ch` }}>
            <span className="font-semibold">{key}:</span> {formatValue(value)}
          </li>
        );
      }
    }
  }
  return items;
};

const SystemLogDetailsDialog: React.FC<SystemLogDetailsDialogProps> = ({ logEntry }) => {
  const { resolveId, loading, error } = useAllLookups();

  if (!logEntry) {
    return (
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log Entry Details</DialogTitle>
          <DialogDescription>No log entry selected.</DialogDescription>
        </DialogHeader>
      </DialogContent>
    );
  }

  let parsedDetails: SystemLogDetails | null = null;
  try {
    parsedDetails = JSON.parse(logEntry.details);
  } catch (e) {
    console.error('Failed to parse log entry details:', e);
    return (
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log Entry Details (ID: {logEntry.id})</DialogTitle>
          <DialogDescription>Error parsing details for this log entry.</DialogDescription>
        </DialogHeader>
        <p className="text-destructive">Raw Details: {logEntry.details}</p>
      </DialogContent>
    );
  }

  const isUpdateWithBeforeAfter = parsedDetails && 'before' in parsedDetails && 'after' in parsedDetails;

  return (
    <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Log Entry Details (ID: {logEntry.id})</DialogTitle>
        <DialogDescription>
          Changes in table: <span className="font-semibold">{logEntry.table_name}</span>
          <br />
          Logged by: <span className="font-semibold">{logEntry.created_by}</span> at{' '}
          <span className="font-semibold">{new Date(logEntry.created_at).toLocaleString()}</span>
        </DialogDescription>
      </DialogHeader>

      {loading ? (
        <div className="flex items-center justify-center h-24">
          <Spinner size="md" /> <span className="ml-2">Loading lookup data...</span>
        </div>
      ) : error ? (
        <p className="text-destructive">Error loading lookup data: {error}</p>
      ) : (
        <div className="py-4 text-sm">
          {isUpdateWithBeforeAfter ? (
            <>
              <h3 className="font-bold text-md mb-2">Before:</h3>
              <ul className="list-disc list-inside">
                {renderDetails(parsedDetails?.before || {}, resolveId, 0, logEntry.table_name)}
              </ul>
              <h3 className="font-bold text-md mt-4 mb-2">After:</h3>
              <ul className="list-disc list-inside">
                {renderDetails(parsedDetails?.after || {}, resolveId, 0, logEntry.table_name)}
              </ul>
            </>
          ) : (
            <>
              <h3 className="font-bold text-md mb-2">Changes:</h3>
              <ul className="list-disc list-inside">
                {renderDetails(parsedDetails || {}, resolveId, 0, logEntry.table_name)}
              </ul>
            </>
          )}
        </div>
      )}
    </DialogContent>
  );
};

export default SystemLogDetailsDialog;